<template>
  <div
    class="my-8 ui-has-state"
    :class="{ 'ui-has-state--is-loading': isLoading }"
  >
    <h2>FOB Price Comparison</h2>
    <div class="md:flex items-center mb-4 -mx-4 flex-wrap">
      <div class="ml-4 mb-2 lg:mx-4 lg:mb-4 text-sm w-full lg:w-auto">
        Select Comparison:
      </div>
      <div class="mx-4 mt-0 mb-8 md:mb-4">
        <label class="visuallyhidden">Range</label>
        <div class="relative">
          <select
            v-model="yearOption"
            @change="chartDataSetup"
            class="mg-form-control mg-select w-full rounded-lg"
          >
            <option
              :value="item.value"
              v-for="(item, index) in rangeOptions"
              :key="index"
              >{{ item.text }}</option
            >
          </select>
          <div
            class="pointer-events-none absolute pin-y pin-r flex items-center px-2 mg-color-accent-2"
          >
            <svg class="fill-current h-8 w-8">
              <use href="#icon-angle-down" xlink:href="#icon-angle-down" />
            </svg>
          </div>
        </div>
      </div>
      <div class="mx-4 mt-0 mb-8 md:mb-4">
        <label class="visuallyhidden">Compare options</label>
        <div class="relative">
          <select
            v-model="comparison"
            class="mg-form-control mg-select w-full rounded-lg test"
            @change="updateDatasets"
          >
            <option
              :value="item.value"
              v-for="(item, index) in compareOptions"
              :key="index"
              >{{ item.text }}</option
            >
          </select>
          <div
            class="pointer-events-none absolute pin-y pin-r flex items-center px-2 mg-color-accent-2"
          >
            <svg class="fill-current h-8 w-8">
              <use href="#icon-angle-down" xlink:href="#icon-angle-down" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <canvas id="fob-price-compare-chart"></canvas>
  </div>
</template>

<script>
import { mapState } from "vuex";

import NProgress from "nprogress";
import { format } from "date-fns";
import numeral from "numeral";

export default {
  name: "fob-price-comparison",
  data() {
    return {
      isLoading: false,
      dataUrl: "/wp-json/mg/v1/fob-price-comparison/",
      yearOption: 2,
      comparison: "gulfDDGS_gulfCorn",
      dataToCompare: [],
      dataForChart: {},
      rangeOptions: [
        {
          text: "Past Year",
          value: 2,
        },
        {
          text: "Past 2 Years",
          value: 3,
        },
      ],
      compareOptions: [
        {
          value: "gulfDDGS_gulfCorn",
          text: "Gulf DDGS vs Gulf Corn",
        },
        {
          value: "gulfCorn_texasSorghum",
          text: "Gulf Corn vs Texas Sorghum",
        },
        // {
        //     value: "gulfCorn_nolaSorghum",
        //     text: "Gulf Corn vs NOLA Sorghum"
        // },
        // {
        //     value: "texasSorghum_nolaSorghum",
        //     text: "Texas Sorghum vs NOLA Sorghum"
        // }
      ],
      config: {
        type: "line",
        data: {
          labels: [],
          datasets: [],
        },
        options: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              padding: 15,
            },
          },
          responsive: true,
          title: {
            display: false,
            text: "Chart.js Line Chart",
          },
          tooltips: {
            mode: "index",
            intersect: false,
            titleFontColor: "#3c3328",
            backgroundColor: "rgba(255,255,255,0.9)",
            xPadding: 8,
            yPadding: 8,
            cornerRadius: 0,
            borderColor: "#ccc",
            borderWidth: 1,
            callbacks: {
              labelTextColor: function(tooltipItem, chart) {
                return "#3c3328";
              },
              label: function(tooltipItem, data) {
                return (
                  data.datasets[tooltipItem.datasetIndex].label +
                  ": " +
                  numeral(tooltipItem.yLabel).format("0,0.0")
                );
              },
            },
          },
          hover: {
            mode: "nearest",
            intersect: true,
          },
          scales: {
            xAxes: [
              {
                display: true,
                scaleLabel: {
                  display: false,
                  labelString: "Date",
                },
              },
            ],
            yAxes: [
              {
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: "Dollars Per Metric Ton",
                },
                ticks: {
                  callback: function(value) {
                    return numeral(value)
                      .format("0a")
                      .toUpperCase();
                  },
                },
              },
            ],
          },
        },
      },
    };
  },
  computed: {
    ...mapState(["colors"]),
    comparisonitems() {
      return this.comparison.split("_");
    },
  },
  methods: {
    getData() {
      let params = {};

      params.year = new Date().getFullYear() - this.yearOption;

      return new Promise((resolve, reject) => {
        this.isLoading = true;
        NProgress.start();
        $.ajax({
          url: this.dataUrl,
          data: params,
        })
          .then(
            (response) => {
              this.dataToCompare = response;
              resolve();
            },
            (error) => {
              console.log(
                "There was an error trying to get FOB price comparison items"
              );
              reject();
            }
          )
          .always(() => {
            this.isLoading = false;
            NProgress.done();
          });
      });
    },
    addLabels() {
      let labels = [];

      this.dataToCompare.forEach((item) => {
        const date = format(item.date, "M/D/YYYY");
        labels.push(date);
      });

      this.config.data.labels = labels;
    },
    updateDatasets() {
      if (!this.comparison) return;

      this.config.data.datasets = [];

      this.dataForChart = {};

      this.comparisonitems.forEach((commodity) => {
        this.dataForChart[commodity] = {
          label: "",
          values: [],
        };
      });

      this.dataToCompare.forEach((item) => {
        this.comparisonitems.forEach((commodity) => {
          if (item.commodities[commodity]) {
            this.dataForChart[commodity]["label"] =
              item.commodities[commodity]["label"];
            this.dataForChart[commodity]["values"].push(
              item.commodities[commodity]["value"]
            );
          }
        });
      });

      this.setSpread();

      Object.keys(this.dataForChart).forEach((key, index) => {
        const color = this.colors[
          this.config.data.datasets.length % this.colors.length
        ];

        let dataset = {
          label: this.dataForChart[key]["label"],
          data: this.dataForChart[key]["values"],
          backgroundColor: color,
          borderColor: color,
          fill: false,
        };

        const blue = "#b0b1e2";
        const red = "#8e1537";
        const gold = "#c06b13";

        if (index == 0) {
          dataset.backgroundColor = blue;
          dataset.borderColor = blue;
        } else if (index == 1) {
          dataset.backgroundColor = red;
          dataset.borderColor = red;
        } else if (index == 2) {
          dataset.backgroundColor = gold;
          dataset.borderColor = gold;
        }

        this.config.data.datasets.push(dataset);
      });

      window.mgFobChart.update();
    },
    setSpread() {
      // 'spread' is the 1st item in `this.comparisonitems`
      // minus the second item.
      if (this.comparisonitems.length <= 1) return;

      const firstCompareItem = this.comparisonitems[0];
      const secondCompareItem = this.comparisonitems[1];

      if (
        this.dataForChart[firstCompareItem]["values"].length !=
        this.dataForChart[secondCompareItem]["values"].length
      ) {
        console.log(
          `${firstCompareItem} does not have the same amount of data entries as ${secondCompareItem}`
        );
        return;
      }

      let spread = {
        label: "Spread",
        values: [],
      };

      this.dataForChart[firstCompareItem]["values"].forEach((value, index) => {
        const otherValue = this.dataForChart[secondCompareItem]["values"][
          index
        ];
        spread.values.push(value - otherValue);
      });

      this.dataForChart.spread = spread;
    },
    chartDataSetup() {
      this.getData().then(() => {
        this.addLabels();
        this.updateDatasets();
      });
    },
    chartInit() {
      const chart = document.querySelector("#fob-price-compare-chart");

      if (!chart) return;

      const ctx = chart.getContext("2d");
      window.mgFobChart = new Chart(ctx, this.config);

      this.chartDataSetup();
    },
  },
  mounted() {
    this.chartInit();
  },
};
</script>

<style scoped>
canvas {
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}
</style>



// WEBPACK FOOTER //
// src/js/vue-components/fob-price-comparison.vue